// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * ShipHealthContext - Centralized Ship Health State Management
 * Replaces global ShipHealthManager singleton with React Context
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import GameConfig from '../config/GameConfig';
import SoundEffects from '../utils/system/SoundEffects';
import EventBus, { EVENT_TYPES } from '../utils/system/EventBus';
import STORAGE_KEYS from '../utils/system/STORAGE_KEYS';

const ShipHealthContext = createContext(null);

// Initialize systems from config
const createInitialSystems = () => {
  const systemDefs = {
    HULL: { name: 'Hull Integrity', maxHealth: GameConfig.shipHealth.startingHealth.hull },
    SHIELDS: { name: 'Shield Generator', maxHealth: GameConfig.shipHealth.startingHealth.shields },
    ENGINES: { name: 'Warp Engines', maxHealth: GameConfig.shipHealth.startingHealth.engines },
    WEAPONS: { name: 'Weapon Systems', maxHealth: GameConfig.shipHealth.startingHealth.weapons },
    LIFE_SUPPORT: { name: 'Life Support', maxHealth: GameConfig.shipHealth.startingHealth.lifeSupport },
    SENSORS: { name: 'Sensor Array', maxHealth: GameConfig.shipHealth.startingHealth.sensors },
    POWER: { name: 'Power Core', maxHealth: GameConfig.shipHealth.startingHealth.power }
  };

  return Object.entries(systemDefs).map(([id, def]) => ({
    id,
    name: def.name,
    maxHealth: def.maxHealth,
    health: def.maxHealth,
  }));
};

export function ShipHealthProvider({ children }) {
  // Initialize from localStorage or defaults
  const [systems, setSystems] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.SHIP_SYSTEMS);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse saved ship systems:', e);
      }
    }
    return createInitialSystems();
  });

  // Track alarm state to prevent multiple alarm loops
  const [alarmPlaying, setAlarmPlaying] = useState(false);
  
  // Track critical system warnings to prevent duplicates
  const [criticalWarnings, setCriticalWarnings] = useState({
    hull: false,
    shields: false,
    power: false
  });

  // Persist to localStorage whenever systems change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.SHIP_SYSTEMS, JSON.stringify(systems));
  }, [systems]);

  // Monitor hull health and play alarm when critical
  useEffect(() => {
    const hullSystem = systems.find(s => s.id === 'HULL');
    if (!hullSystem) return;

    const hullPercent = (hullSystem.health / hullSystem.maxHealth) * 100;
    const isCritical = hullPercent < 20;

    if (isCritical && !alarmPlaying) {
      // Start alarm loop
      SoundEffects.play('alarm_loop', { loop: true, volume: 0.3 });
      setAlarmPlaying(true);
      
      // Play hull_breach voice announcement (once)
      if (!criticalWarnings.hull) {
        EventBus.emit(EVENT_TYPES.HULL_BREACH);
        setCriticalWarnings(prev => ({ ...prev, hull: true }));
      }
    } else if (!isCritical && alarmPlaying) {
      // Stop alarm loop
      SoundEffects.stop('alarm_loop');
      setAlarmPlaying(false);
    }
    
    // Reset hull warning if recovered above 30%
    if (hullPercent > 30 && criticalWarnings.hull) {
      setCriticalWarnings(prev => ({ ...prev, hull: false }));
    }
  }, [systems, alarmPlaying, criticalWarnings.hull]);
  
  // Monitor shields and power for critical warnings
  useEffect(() => {
    const shieldsSystem = systems.find(s => s.id === 'SHIELDS');
    const powerSystem = systems.find(s => s.id === 'POWER');
    
    if (shieldsSystem) {
      const shieldsPercent = (shieldsSystem.health / shieldsSystem.maxHealth) * 100;
      const isCritical = shieldsPercent < 20;
      
      if (isCritical && !criticalWarnings.shields) {
        EventBus.emit(EVENT_TYPES.SHIELDS_CRITICAL);
        setCriticalWarnings(prev => ({ ...prev, shields: true }));
      } else if (shieldsPercent > 30 && criticalWarnings.shields) {
        setCriticalWarnings(prev => ({ ...prev, shields: false }));
      }
    }
    
    if (powerSystem) {
      const powerPercent = (powerSystem.health / powerSystem.maxHealth) * 100;
      const isCritical = powerPercent < 20;
      
      if (isCritical && !criticalWarnings.power) {
        EventBus.emit(EVENT_TYPES.POWER_CRITICAL);
        setCriticalWarnings(prev => ({ ...prev, power: true }));
      } else if (powerPercent > 30 && criticalWarnings.power) {
        setCriticalWarnings(prev => ({ ...prev, power: false }));
      }
    }
  }, [systems, criticalWarnings.shields, criticalWarnings.power]);

  // Get all systems
  const getAllSystems = useCallback(() => {
    return systems;
  }, [systems]);

  // Get a specific system by ID
  const getSystem = useCallback((systemId) => {
    return systems.find(s => s.id === systemId);
  }, [systems]);

  // Get system health (0-100)
  const getSystemHealth = useCallback((systemId) => {
    const system = systems.find(s => s.id === systemId);
    return system ? system.health : 0;
  }, [systems]);

  // Get system health percentage
  const getSystemHealthPercent = useCallback((systemId) => {
    const system = systems.find(s => s.id === systemId);
    if (!system) return 0;
    return (system.health / system.maxHealth) * 100;
  }, [systems]);

  // Damage a system
  const damageSystem = useCallback((systemId, amount) => {
    setSystems(prev => prev.map(sys => {
      if (sys.id === systemId) {
        const newHealth = Math.max(0, sys.health - amount);
        
        // Play appropriate damage sound
        if (systemId === 'SHIELDS') {
          SoundEffects.play('shield_hit', { volume: 0.6 });
        } else if (systemId === 'HULL') {
          SoundEffects.play('hull_hit', { volume: 0.7 });
        } else {
          // Other systems use generic impact sound
          SoundEffects.play('hull_hit', { volume: 0.5 });
        }
        
        return { ...sys, health: newHealth };
      }
      return sys;
    }));
  }, []);

  // Repair a system to full health
  const repairSystem = useCallback((systemId) => {
    setSystems(prev => prev.map(sys => {
      if (sys.id === systemId) {
        const wasDamaged = sys.health < sys.maxHealth;
        
        // Play repairs_complete voice if system was damaged
        if (wasDamaged) {
          setTimeout(() => {
            EventBus.emit(EVENT_TYPES.REPAIRS_COMPLETE);
          }, 500);
        }
        
        return { ...sys, health: sys.maxHealth };
      }
      return sys;
    }));
    
    // Reset critical warning for this system
    if (systemId === 'HULL') {
      setCriticalWarnings(prev => ({ ...prev, hull: false }));
    } else if (systemId === 'SHIELDS') {
      setCriticalWarnings(prev => ({ ...prev, shields: false }));
    } else if (systemId === 'POWER') {
      setCriticalWarnings(prev => ({ ...prev, power: false }));
    }
  }, []);

  // Repair all systems
  const repairAll = useCallback(() => {
    const hadDamage = systems.some(sys => sys.health < sys.maxHealth);
    
    setSystems(prev => prev.map(sys => ({
      ...sys,
      health: sys.maxHealth
    })));
    
    // Play repairs_complete voice if there was damage
    if (hadDamage) {
      setTimeout(() => {
        EventBus.emit(EVENT_TYPES.REPAIRS_COMPLETE);
        
        // Check if all systems are now nominal
        const allNominal = systems.every(sys => sys.health === sys.maxHealth);
        if (allNominal) {
          setTimeout(() => {
            EventBus.emit(EVENT_TYPES.ALL_SYSTEMS_NOMINAL);
          }, 2000); // Play 2 seconds after repairs complete
        }
      }, 500);
    }
    
    // Reset all critical warnings
    setCriticalWarnings({ hull: false, shields: false, power: false });
    
    return systems.map(sys => ({ id: sys.id, name: sys.name }));
  }, [systems]);

  // Check if system is critical
  const isSystemCritical = useCallback((systemId) => {
    const system = systems.find(s => s.id === systemId);
    if (!system) return false;
    const percent = (system.health / system.maxHealth) * 100;
    const threshold = GameConfig.shipHealth.criticalThresholds[systemId.toLowerCase()] || 30;
    return percent <= threshold;
  }, [systems]);

  // Check if system is damaged
  const isSystemDamaged = useCallback((systemId) => {
    const system = systems.find(s => s.id === systemId);
    if (!system) return false;
    return system.health < system.maxHealth;
  }, [systems]);

  // Get overall ship health percentage
  const getOverallHealth = useCallback(() => {
    const totalHealth = systems.reduce((sum, sys) => sum + sys.health, 0);
    const totalMaxHealth = systems.reduce((sum, sys) => sum + sys.maxHealth, 0);
    return (totalHealth / totalMaxHealth) * 100;
  }, [systems]);

  // Get critical systems count
  const getCriticalSystemsCount = useCallback(() => {
    return systems.filter(sys => {
      const percent = (sys.health / sys.maxHealth) * 100;
      const threshold = GameConfig.shipHealth.criticalThresholds[sys.id.toLowerCase()] || 30;
      return percent <= threshold;
    }).length;
  }, [systems]);

  // Get damaged systems count
  const getDamagedSystemsCount = useCallback(() => {
    return systems.filter(sys => sys.health < sys.maxHealth).length;
  }, [systems]);

  // Reset to defaults (for game reset)
  const resetShipHealth = useCallback(() => {
    setSystems(createInitialSystems());
    localStorage.removeItem(STORAGE_KEYS.SHIP_SYSTEMS);
    setCriticalWarnings({ hull: false, shields: false, power: false });
    setAlarmPlaying(false);
    SoundEffects.stop('alarm_loop');
  }, []);

  const value = {
    systems,
    getAllSystems,
    getSystem,
    getSystemHealth,
    getSystemHealthPercent,
    damageSystem,
    repairSystem,
    repairAll,
    isSystemCritical,
    isSystemDamaged,
    getOverallHealth,
    getCriticalSystemsCount,
    getDamagedSystemsCount,
    resetShipHealth,
  };

  return (
    <ShipHealthContext.Provider value={value}>
      {children}
    </ShipHealthContext.Provider>
  );
}

// Custom hook to use ship health context
export function useShipHealth() {
  const context = useContext(ShipHealthContext);
  if (!context) {
    throw new Error('useShipHealth must be used within a ShipHealthProvider');
  }
  return context;
}

export default ShipHealthContext;
